import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ArrowRight, Heart, Users, Brain, Clock, MapPin, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';

// Testimonial Carousel Component
const TestimonialCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const testimonials = [
    {
      quote: "Anvita is a great therapist and a very competent counsellor. She creates a safe space that enables one to talk about the problems that they face without any hesitation or fear of judgment. She doesn't dictate what you should do but rather leads you to find what is right for you. Sessions with her made me feel a lot better and eventually helped me see the greener side for the future.",
      name: "AJ",
      details: "27 years, Finance Professional"
    },
    {
      quote: "Anvita is very knowledgeable in her field. I had been to several other counsellors in the United States – I can say with confidence that Anvita is a class apart. What truly sets Anvita apart is her resourcefulness and creative ability to come up with solutions based on sound scientific research. She takes time to summarize the proposed solutions at the end of the session and provides actionable items to work on. Additionally, on more than one occasion, she has been very accommodative of schedules and we greatly appreciate her flexibility.",
      name: "KS & HS",
      details: "Couples Therapy, Doctors"
    },
    {
      quote: "Anvita is a wonderful therapist. This was my first experience with therapy but just after a few sessions with her, it left me wondering why I did not seek help earlier. What I enjoyed most about my sessions with her was that she would guide me at my exact level of understanding leading to very deep and interesting conversations. Our sessions were just what I needed in my time of crisis and now I feel adequately equipped to deal with anything coming my way. I would recommend her to everyone.",
      name: "NJ",
      details: "42 years, Radio Jockey"
    }
  ];

  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, testimonials.length]);

  const goToPrevious = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const goToSlide = (index) => {
    setIsAutoPlaying(false);
    setCurrentIndex(index);
  };

  return (
    <div className="relative">
      {/* Main Testimonial */}
      <div className="relative min-h-[280px] flex items-center justify-center">
        <div 
          className="text-center px-4 md:px-12 transition-opacity duration-500"
          key={currentIndex}
        >
          <div className="text-6xl font-heading italic opacity-30 mb-6">"</div>
          <blockquote className="text-lg md:text-xl leading-relaxed mb-8 text-white/95">
            {testimonials[currentIndex].quote}
          </blockquote>
          <div>
            <p className="font-heading font-medium text-lg">— {testimonials[currentIndex].name}</p>
            <p className="text-white/60 text-sm">{testimonials[currentIndex].details}</p>
          </div>
        </div>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={goToPrevious}
        className="absolute left-0 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        aria-label="Previous testimonial"
      >
        <ChevronLeft size={24} />
      </button>
      <button
        onClick={goToNext}
        className="absolute right-0 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        aria-label="Next testimonial"
      >
        <ChevronRight size={24} />
      </button>

      {/* Dots Navigation */}
      <div className="flex justify-center gap-2 mt-8">
        {testimonials.map((_, idx) => (
          <button
            key={`dot-${idx}`}
            onClick={() => goToSlide(idx)}
            className={`w-2.5 h-2.5 rounded-full transition-all ${
              idx === currentIndex 
                ? 'bg-white w-8' 
                : 'bg-white/30 hover:bg-white/50'
            }`}
            aria-label={`Go to testimonial ${idx + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

const Home = () => {
  const [location, setLocation] = useState('');
  const [detectingLocation, setDetectingLocation] = useState(true);

  useEffect(() => {
    const detectLocation = async () => {
      setDetectingLocation(true);
      
      if (!navigator.geolocation) {
        setDetectingLocation(false);
        setLocation('international');
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const { latitude, longitude } = position.coords;
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&accept-language=en`
            );
            const data = await response.json();
            
            const country = data.address?.country || data.address?.country_code;
            const isIndia = country?.toLowerCase() === 'india' || data.address?.country_code?.toLowerCase() === 'in';
            
            setLocation(isIndia ? 'india' : 'international');
            setDetectingLocation(false);
          } catch (error) {
            console.error('Error detecting location:', error);
            setLocation('international');
            setDetectingLocation(false);
          }
        },
        (error) => {
          console.log('Geolocation error:', error.message);
          setLocation('international');
          setDetectingLocation(false);
        },
        {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 300000
        }
      );
    };

    detectLocation();
  }, []);

  const services = [
    {
      title: 'Individual Therapy',
      priceINR: '₹3,000',
      priceUSD: '$60',
      duration: '60 min',
      description: 'One-on-one sessions tailored to your unique needs and goals.',
      image: 'https://images.unsplash.com/photo-1610905277069-8cd4a85fbff2?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800',
      icon: Heart
    },
    {
      title: 'Couples Therapy',
      priceINR: '₹4,000',
      priceUSD: '$80',
      duration: '75 min',
      description: 'Strengthen your relationship and improve communication.',
      image: 'https://images.unsplash.com/photo-1591630060069-7cb1f16261b4?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800',
      icon: Users
    },
    {
      title: 'Adolescent Therapy',
      priceINR: '₹3,500',
      priceUSD: '$70',
      duration: '60 min',
      description: 'A safe space for young people aged 12–18 to explore emotions.',
      image: 'https://images.pexels.com/photos/112361/pexels-photo-112361.jpeg?auto=compress&cs=tinysrgb&w=800',
      icon: Brain
    },
  ];

  const focusAreas = [
    'Relationship Challenges', 'Anxiety & Stress', 'Complex PTSD',
    'Childhood Trauma & Neglect', 'Depression', 'Body Image & Eating Disorders',
    'Marriage Counselling', 'Adults with ADHD', 'Grief & Loss',
    'Gender & Sexuality', 'Major Life Transitions'
  ];

  const getPrice = (service) => {
    return location === 'india' ? service.priceINR : service.priceUSD;
  };

  return (
    <div className="pt-20" data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-section relative min-h-[90vh] flex items-center px-6 py-20" data-testid="hero-section">
        <div className="floating-blob w-96 h-96 bg-primary/20 top-20 -left-48" style={{ animationDelay: '0s' }}></div>
        <div className="floating-blob w-80 h-80 bg-accent/20 bottom-20 -right-40" style={{ animationDelay: '7s' }}></div>
        
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center relative z-10">
          <div className="space-y-8">
            <h1 className="text-5xl md:text-7xl font-heading font-light tracking-tight text-foreground" data-testid="hero-heading">
              Untangle thoughts.
              <br />
              <span className="text-primary italic">Find clarity.</span>
              <br />
              Heal with compassion.
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground font-body leading-relaxed">
              I help people navigate relationships with more clarity and compassion through deep conversations, thoughtful insights, and a little bit of humor.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/discovery">
                <Button className="bg-primary text-white hover:bg-primary/90 rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all" data-testid="hero-discovery-button">
                  Book a Discovery Call <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/book">
                <Button variant="outline" className="rounded-full px-8 py-6 text-lg border-2 border-primary text-primary hover:bg-primary/5" data-testid="hero-book-button">
                  Book a Session
                </Button>
              </Link>
            </div>
          </div>
          <div className="relative">
            <img
              src="https://customer-assets.emergentagent.com/job_mindful-sessions-13/artifacts/h6hfwqko_WhatsApp%20Image%202026-03-24%20at%2012.25.15.jpeg"
              alt="Serene forest path winding through tall trees"
              className="rounded-3xl shadow-2xl w-full h-[600px] object-cover"
            />
          </div>
        </div>
      </section>

      {/* How It Works - Booking Process */}
      <section className="py-24 px-6 bg-secondary/30" data-testid="how-it-works-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-heading font-normal text-foreground mb-4">
              How It Works
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Your journey to healing begins with three simple steps
            </p>
          </div>

          {/* Steps Container */}
          <div className="relative">
            {/* Connection Line - Hidden on mobile */}
            <div className="hidden md:block absolute top-24 left-[16%] right-[16%] h-0.5 bg-gradient-to-r from-primary/20 via-primary to-primary/20"></div>

            <div className="grid md:grid-cols-3 gap-8 md:gap-12">
              {/* Step 1 */}
              <div className="relative flex flex-col items-center text-center group">
                <div className="w-20 h-20 rounded-full bg-primary flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform">
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                  </svg>
                </div>
                <div className="absolute top-6 -right-4 md:right-auto md:-top-2 md:left-1/2 md:transform md:-translate-x-1/2 w-8 h-8 rounded-full bg-accent text-white flex items-center justify-center text-sm font-bold shadow-md">
                  1
                </div>
                <h3 className="text-xl font-heading font-medium text-foreground mb-3">
                  Book a Discovery Call
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Schedule a free 15-minute call to see if we're a good fit and understand how therapy works with me.
                </p>
                <Link to="/discovery" className="mt-4 text-primary font-medium hover:underline inline-flex items-center gap-1">
                  Schedule Now
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                </Link>
              </div>

              {/* Step 2 */}
              <div className="relative flex flex-col items-center text-center group">
                <div className="w-20 h-20 rounded-full bg-primary flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform">
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect width="18" height="18" x="3" y="4" rx="2" ry="2"/>
                    <line x1="16" x2="16" y1="2" y2="6"/>
                    <line x1="8" x2="8" y1="2" y2="6"/>
                    <line x1="3" x2="21" y1="10" y2="10"/>
                    <path d="m9 16 2 2 4-4"/>
                  </svg>
                </div>
                <div className="absolute top-6 -right-4 md:right-auto md:-top-2 md:left-1/2 md:transform md:-translate-x-1/2 w-8 h-8 rounded-full bg-accent text-white flex items-center justify-center text-sm font-bold shadow-md">
                  2
                </div>
                <h3 className="text-xl font-heading font-medium text-foreground mb-3">
                  Schedule Your Session
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Choose a time that works for you from the available slots and complete the booking with payment.
                </p>
                <Link to="/book" className="mt-4 text-primary font-medium hover:underline inline-flex items-center gap-1">
                  View Available Slots
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                </Link>
              </div>

              {/* Step 3 */}
              <div className="relative flex flex-col items-center text-center group">
                <div className="w-20 h-20 rounded-full bg-primary flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform">
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
                    <path d="M15 2H9a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1z"/>
                    <path d="M12 11h4"/>
                    <path d="M12 16h4"/>
                    <path d="M8 11h.01"/>
                    <path d="M8 16h.01"/>
                  </svg>
                </div>
                <div className="absolute top-6 -right-4 md:right-auto md:-top-2 md:left-1/2 md:transform md:-translate-x-1/2 w-8 h-8 rounded-full bg-accent text-white flex items-center justify-center text-sm font-bold shadow-md">
                  3
                </div>
                <h3 className="text-xl font-heading font-medium text-foreground mb-3">
                  Complete Intake Form
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Fill out the intake form before your first session so we can make the most of our time together.
                </p>
                <a 
                  href="https://docs.google.com/forms/d/e/1FAIpQLSccQdWvf4Vi7SYZh_2ijAZPKB0qsUY58NXGlwX0gc2VTK_hwA/viewform"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 text-primary font-medium hover:underline inline-flex items-center gap-1"
                >
                  Access Form
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" x2="21" y1="14" y2="3"/></svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-6" data-testid="services-section">
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between items-end mb-12">
            <div className="space-y-3">
              <h2 className="text-3xl md:text-4xl font-heading font-normal text-foreground">
                How I Can Support You
              </h2>
              <p className="text-muted-foreground max-w-xl">
                Whether you're navigating relationships, healing from trauma, or seeking personal growth,
                I offer a safe space for transformation.
              </p>
            </div>
            
            {/* Hidden Location Toggle */}
            <details className="group relative">
              <summary className="flex items-center gap-2 cursor-pointer list-none text-sm text-muted-foreground hover:text-foreground transition-colors">
                <MapPin size={14} />
                {detectingLocation ? (
                  <span className="animate-pulse">Detecting...</span>
                ) : (
                  <span>{location === 'india' ? 'INR' : 'USD'}</span>
                )}
                <ChevronDown size={14} className="group-open:rotate-180 transition-transform" />
              </summary>
              <div className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-lg border p-4 z-20 w-56">
                <p className="text-xs text-muted-foreground mb-3">
                  Update your location:
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setLocation('india')}
                    className={`p-2 rounded-lg border text-xs transition-all ${
                      location === 'india' 
                        ? 'border-primary bg-primary/5 text-primary font-medium' 
                        : 'border-muted text-muted-foreground hover:border-primary/30'
                    }`}
                    data-testid="home-location-india"
                  >
                    India (INR)
                  </button>
                  <button
                    onClick={() => setLocation('international')}
                    className={`p-2 rounded-lg border text-xs transition-all ${
                      location === 'international' 
                        ? 'border-primary bg-primary/5 text-primary font-medium' 
                        : 'border-muted text-muted-foreground hover:border-primary/30'
                    }`}
                    data-testid="home-location-international"
                  >
                    International (USD)
                  </button>
                </div>
              </div>
            </details>
          </div>

          {/* Service Cards - Medium Size */}
          <div className="grid md:grid-cols-3 gap-6">
            {services.map((service) => {
              const Icon = service.icon;
              return (
                <Card
                  key={service.title}
                  className="overflow-hidden rounded-2xl border shadow-sm hover:shadow-xl transition-all bg-white group"
                  data-testid={`service-card-${service.title.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 flex items-center gap-2">
                      <div className="bg-white/20 backdrop-blur-sm rounded-full p-2">
                        <Icon size={18} className="text-white" />
                      </div>
                      <h3 className="text-lg font-heading font-medium text-white">{service.title}</h3>
                    </div>
                    {/* Duration Badge */}
                    <div className="absolute top-3 right-3 flex items-center gap-1 text-xs text-white bg-black/40 backdrop-blur-sm px-2.5 py-1 rounded-full">
                      <Clock size={11} />
                      {service.duration}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-5">
                    {/* Price */}
                    <div className="flex items-baseline gap-2 mb-3">
                      <span className="text-2xl font-heading font-bold text-accent">
                        {detectingLocation ? '...' : getPrice(service)}
                      </span>
                      <span className="text-xs text-muted-foreground">per session</span>
                    </div>

                    {/* Description */}
                    <p className="text-sm text-muted-foreground mb-4">
                      {service.description}
                    </p>

                    {/* Book Button */}
                    <Link to="/book">
                      <Button 
                        className="w-full bg-primary text-white hover:bg-primary/90 rounded-full py-5 text-sm" 
                        data-testid={`book-${service.title.toLowerCase().replace(' ', '-')}`}
                      >
                        Book Now <ArrowRight className="ml-1" size={16} />
                      </Button>
                    </Link>
                  </div>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-10">
            <Link to="/services">
              <Button variant="outline" className="rounded-full px-8 py-5 text-base border-2 border-accent text-accent hover:bg-accent/5" data-testid="view-all-services-button">
                View All Services <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Focus Areas */}
      <section className="py-20 px-6 bg-secondary/30" data-testid="focus-areas-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-normal text-foreground mb-4">
              Together, We Can Work On
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {focusAreas.map((area, idx) => (
              <div
                key={`focus-${area.substring(0, 20)}`}
                className="bg-white/60 backdrop-blur-sm border border-muted rounded-xl p-5 text-center hover:bg-white/90 hover:shadow-md transition-all"
                data-testid={`focus-area-${idx}`}
              >
                <p className="text-foreground font-medium text-sm">{area}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section - Carousel */}
      <section className="py-20 px-6 bg-primary text-white overflow-hidden" data-testid="testimonial-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-normal mb-3">
              What Clients Say
            </h2>
            <p className="text-white/70">Real experiences from real people</p>
          </div>
          
          <TestimonialCarousel />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center space-y-6">
          <h2 className="text-3xl md:text-4xl font-heading font-normal text-foreground">
            Not Sure Where to Start?
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Book a free 15-minute discovery call. We'll chat about what you're going through and see if we're a good fit to work together. No pressure, no obligation.
          </p>
          <div className="flex justify-center">
            <Link to="/discovery">
              <Button className="bg-accent text-white hover:bg-accent/90 rounded-full px-10 py-6 text-lg shadow-lg hover:shadow-xl transition-all" data-testid="cta-discovery-button">
                Book a Discovery Call
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
